// Public API (FC-183 §2): initializeAnalytics for the host application (once
// per page), useAnalytics for a composed application (once per application),
// registerAnalytics for a surface that is sometimes each.
export {
  initializeAnalytics,
  registerAnalytics,
  updateSystemProperties,
  useAnalytics,
} from './initialize'
export { onConsentChanged } from './one-trust-cookie-banner'
export type { PageContext } from './page-context'
export { updatePageContext } from './page-context'
export { registerProductAdapter, resolveProduct } from './product-adapter'

// The shared attribution set, keyed the way Avo's generated clients name their
// parameters. The client already attaches it at registration; this export is
// for a surface that must re-read it per event because its values change
// mid-session (Returns: location_id and page_type move as the flow advances,
// and a deferred dispatch must send its own values, not the next event's).
// Build on this rather than hand-rolling the rename — that is how the set
// drifted per application before FC-183.
export { currentCommonSystemProperties } from './system-properties'

// Where a surface is mounted — the one thing an application declares. The
// Segment source, the consent posture, the Avo environment and the GA4
// measurement id are all derived from it, never passed in.
export {
  isProductionEnvironment,
  normalizeAnalyticsEnvironment,
  ONE_TRUST_DOMAIN_SCRIPT_ID,
  SEGMENT_SOURCES,
} from './sources'

// The transport instance stays available for identity calls
// (analytics.identify / analytics.reset / analytics.ready). The readiness
// signals it once carried are gone (FC-186 done-when 5): fire and forget,
// the client buffers.
export { analytics } from './initialize'

// Consent constants and readers are defined once, in @susu/cookie (FC-183
// D16/O5), and only re-exported here.
export {
  FBP_COOKIE_NAME,
  GA_COOKIE_NAME,
  getGaCookieId,
  IDENTITY_KEY_ANONYMOUS,
  IDENTITY_KEY_KNOWN,
  IDENTITY_VALUE,
  setGaCookieId,
} from './constants'
export {
  getOneTrustConsent,
  getOneTrustConsentPreferences,
  OPTANON_COOKIE_NAME,
  OPTANON_DEFAULT_CONSENT,
  setAssumedConsent,
} from '@susu/cookie'

// Utilities
export { isSfccHostedPage } from './env'
export { createGAdata, createPromiseFromSignal, errorHandler } from './utils'

/**
 * Google Analytics cookie readers, re-exported from `@susu/segment-common`.
 *
 * GA4 rides on Segment as a destination — no application loads gtag or
 * configures a measurement id. The client resolves the id from the surface's
 * environment and these read the resulting `_ga` / `_ga_<id>` cookies.
 */
export type { AnalyticsEnvironment } from '@susu/segment-common'
export {
  getGA4ClientIdFromCookie,
  getGA4SessionDataFromCookie,
} from '@susu/segment-common'

// Plugins
export { integrationPlugin } from './plugins'

// Avo destination
export { createSegmentDestination, segmentDestination } from './avo-destination'

// Types
export type { SegmentDestination } from './avo-destination'
export type { Placement } from './sources'
export type {
  AnalyticsSurface,
  ApplicationAnalyticsOptions,
  AvoSystemProperties,
  CanonicalProduct,
  ConsentPosture,
  InitAvo,
  InitializeAnalyticsOptions,
  ProductAdapter,
  RegisterAnalyticsOptions,
  SegmentLoadOptions,
  UseAnalyticsOptions,
} from './types'

// OneTrust cookie banner. @deprecated as a direct call — the client inserts the
// consent script itself for a website placement.
export { insertCookieTrackingScript } from './one-trust-cookie-banner'
